import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as clipboard from '@attain-apps/fe/web/main/lib/clipboard'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as excel from '@attain-apps/fe/web/main/lib/excel'
import { TypeChecker } from '@attain-apps/type-guards'

import * as tableModels from '../models'

import { Config } from './config'
import { Plugin } from './plugin'

/**
 * Creates a branch-stable table snapshot signal for one entity collection.
 *
 * The adapter captures collection-wide mapping rules once and receives one
 * view-specific signal per table instance. It creates separate computed
 * signals for rows, columns, pagination, search, selection, tools, and
 * status. The final config signal composes those branch references for the
 * table store.
 */
export function createConfigSg<
  TTypeParams extends ec.models.TypeParams.Constraint,
>(
  collection: ec.Collection<TTypeParams>,
  viewSg: ngCore.Signal<Config.View<TTypeParams> | undefined>,
): ngCore.Signal<tableModels.Config<TTypeParams['entity']>> {
  const plugin = collection.plugins.lookup(Plugin<TTypeParams>).get()
  const currentList = collection.plugins.currentListPlugin
  const pluginConfig = plugin.config
  // Keep selector functions, not selector values, so each computed branch can
  // subscribe to only the state it actually consumes.
  const attributesSg =
    collection.plugins.attributePlugin.feature.selectors.attributes.asSignal
  const visibleAttributesSg =
    collection.plugins.attributePlugin.feature.selectors.visibleAttributes
      .asSignal
  const areAttributesLoadingSg =
    collection.plugins.attributePlugin.feature.selectors.isLoading.asSignal
  const currentIdsSg = currentList.feature.selectors.ids.asSignal
  const entitiesSg = currentList.feature.selectors.entities.asSignal
  const errorSg = currentList.feature.selectors.error.asSignal
  const isLoadingSg = currentList.feature.selectors.isLoading.asSignal
  const matchedCountSg = currentList.feature.selectors.matchedCount.asSignal
  const queryRequestSg = currentList.feature.selectors.queryRequest.asSignal
  // The default target preserves ordinary collection tables, while a view can
  // opt into a feature-specific target when another part of the page consumes
  // the same selection lookup (for example, Space bulk actions).
  const selectionTargetSg = ngCore.computed(
    (view = viewSg()) => view?.selectionTarget ?? ec.models.Target.DEFAULT,
  )
  const selectedIdsSg = ngCore.computed(
    (selectionTarget = selectionTargetSg()) =>
      collection.plugins.selectionPlugin.feature.selectors
        .getAllSelectedIds({
          target: selectionTarget,
        })
        .asSignal(),
  )
  const hasCount = collection.plugins.apiExecutionPlugin.canExecute('count')
  const clipboardPlugin = collection.plugins
    .lookup(clipboard.Plugin<TTypeParams>)
    .tryGet()
  const excelPlugin = collection.plugins
    .lookup(excel.ec.Plugin<TTypeParams>)
    .tryGet()
  const canPasteSg = clipboardPlugin?.feature.selectors.canPaste.asSignal
  const isDownloadingSg = excelPlugin?.feature.selectors.isDownloading.asSignal
  const emptyEntities: readonly TTypeParams['entity'][] = []
  const emptyIds: readonly ec.models.EntityId[] = []
  // Query request is one object in entity-collection state. These projections
  // split it into independent dependencies so pagination changes do not
  // invalidate sorting or search, and vice versa.
  const sortSg = ngCore.computed(
    (queryRequest = queryRequestSg()) => queryRequest.sort,
  )
  const paginationRequestSg = ngCore.computed(
    (queryRequest = queryRequestSg()) => queryRequest.pagination,
  )
  const searchValueSg = ngCore.computed(
    (queryRequest = queryRequestSg()) => queryRequest.filter?.searchTerm ?? '',
  )
  // Rows are suppressed when there is no current ID or no visible attribute.
  // This preserves the existing table empty-state behavior without rebuilding
  // entity arrays while the table has no renderable data.
  const hasDataSg = ngCore.computed(
    (currentIds = currentIdsSg(), attributes = visibleAttributesSg()) =>
      currentIds.length > 0 && attributes.length > 0,
  )
  const layoutSg = ngCore.computed(
    (view = viewSg()): tableModels.Layout => ({
      container: view?.container,
      showColumnSettings: view?.showColumnSettings,
      title: view?.title,
      wrap: view?.wrap,
    }),
  )
  // Row callbacks and identity are static adapter behavior. Only `items` is
  // signal-driven, so row description functions do not get recreated for a
  // loading, search, or pagination-only update.
  const rowsWithoutItems = {
    describe: ({
      row,
      rowId,
      rowIndex,
    }: tableModels.Row.Context<TTypeParams['entity']>) => {
      const description = pluginConfig.rowConfig({
        entity: row,
        rowIdx: rowIndex,
      })
      return {
        tools: description.iconsConfigs?.map((iconConfig, toolIndex) =>
          getRowTool(rowId, toolIndex, iconConfig),
        ),
        onClick: description.onClick,
        styles: description.css,
      }
    },
    getId: (entity: TTypeParams['entity']) => collection.identify(entity),
  }
  const emptyRows: tableModels.Row.Config<TTypeParams['entity']> = {
    ...rowsWithoutItems,
    items: emptyEntities,
  }
  // The conditional read is intentional: when `hasData` is false, entities are
  // not read and later entity updates cannot invalidate the stable empty-row
  // config until IDs/visible attributes make data renderable again.
  const rowsSg = ngCore.computed((hasData = hasDataSg()) =>
    hasData
      ? {
          ...rowsWithoutItems,
          items: entitiesSg(),
        }
      : emptyRows,
  )
  // Column objects are rebuilt only when attributes or sort direction changes.
  // A page response therefore preserves the exact columns-array reference used
  // by Store.setConfig for its reconciliation fast path.
  const columnsSg = ngCore.computed(
    (attributes = attributesSg(), sort = sortSg()) =>
      attributes.map(attribute =>
        getColumn({ attribute, collection, config: pluginConfig, sort }),
      ),
  )
  /** Maps controlled pagination without-count and known-count sources. */
  const paginationSg = ngCore.computed(
    (view = viewSg()): tableModels.Pagination | undefined => {
      if (!view?.pagination) {
        // Do not subscribe to query or entity signals when pagination is not a
        // configured table feature.
        return undefined
      }

      const paginationRequest = paginationRequestSg()
      const pageSize = paginationRequest.quantity
      if (pageSize === undefined) {
        return undefined
      }

      const page =
        ents.core.common.Request.For.Pagination.ByPosition.getPageNumber({
          quantity: pageSize,
          skip: paginationRequest.skip,
        })
      // DMS can represent an empty count response as an empty array. Keep
      // that transport value out of the table contract instead of letting
      // JavaScript coerce it to zero in the range formatter.
      const matchedCountValue = hasCount ? matchedCountSg() : undefined
      const matchedCount = TypeChecker.isNumber(matchedCountValue)
        ? matchedCountValue
        : undefined
      const pageCount =
        matchedCount === undefined
          ? 1
          : Math.max(Math.ceil(matchedCount / pageSize), 1)

      // With a count, page availability is derived from the total. Without a
      // count, the adapter can only say that another page exists when the
      // current response filled the requested page size.
      return {
        hasNext: hasCount ? page < pageCount : entitiesSg().length >= pageSize,
        hasPrevious: page > 1,
        isPageInputDisabled: hasCount && pageCount === 1,
        onPageChange: nextPage =>
          currentList.appliedParams.actions.setSkip
            .from('DsTable.Pagination.Page.Changed')
            .dispatch(
              ents.core.common.Request.For.Pagination.ByPosition.getSkip({
                pageNumber: nextPage,
                quantity: pageSize,
              }),
            ),
        onPageSizeChange: nextPageSize =>
          currentList.appliedParams.actions.setQuantity
            .from('DsTable.Pagination.PageSize.Changed')
            .dispatch(nextPageSize),
        page,
        pageSize,
        supportedPageSizes:
          view.supportedPageSizes ?? Config.View.DEFAULT_SUPPORTED_PAGE_SIZES,
        totalCount: matchedCount,
      }
    },
  )
  const searchSg = ngCore.computed((): tableModels.Search | undefined => {
    const search = viewSg()?.search
    if (!search) {
      return undefined
    }

    return {
      onChange: nextValue =>
        currentList.appliedParams.actions.setSearch
          .from('DsTable.Search.Changed')
          .dispatch(nextValue || undefined),
      options: TypeChecker.isObject(search) ? search : undefined,
      value: searchValueSg(),
    }
  })
  // Selection state must include current IDs and selected IDs because both are
  // needed to derive allSelected/someSelected. The callbacks read currentIdsSg
  // at event time so a retained callback never dispatches stale page IDs.
  const selectionSg = ngCore.computed(
    (): tableModels.Selection<TTypeParams['entity']> | undefined => {
      if (!viewSg()?.selection) {
        return undefined
      }

      const currentIds = currentIdsSg()
      const selectionTarget = selectionTargetSg()
      const selectedIds = selectedIdsSg()
      return {
        allSelected:
          currentIds.length > 0 &&
          currentIds.every(id => selectedIds.includes(id)),
        onAllChange: selected =>
          collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
            .from('DsTable.Selection.All.Changed')
            .dispatch({
              key: selectionTarget,
              value: selected ? { set: currentIdsSg() } : { release: true },
            }),
        onRowChange: (rowId, selected) => {
          if (!ec.models.EntityId.isEntityId(rowId)) {
            return
          }
          collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
            .from(`DsTable.Selection.Row-${rowId}.Changed`)
            .dispatch({
              key: selectionTarget,
              value: selected ? { add: rowId } : { remove: rowId },
            })
        },
        selectedIds,
        someSelected: currentIds.some(id => selectedIds.includes(id)),
      }
    },
  )
  const toolsSg = ngCore.computed(() =>
    getTools({
      canPaste: canPasteSg?.(),
      clipboardPlugin,
      collection,
      excelPlugin,
      isDownloading: isDownloadingSg?.(),
      selectedIds: clipboardPlugin ? selectedIdsSg() : emptyIds,
      view: viewSg(),
    }),
  )
  // Error identity is reduced to a boolean because the canonical status model
  // exposes only an error-present state, not the collection's error payload.
  const hasErrorSg = ngCore.computed((error = errorSg()) => error !== undefined)
  const statusWithoutState = {
    empty: {},
    onRetry: () =>
      currentList.feature.actions.load
        .from('DsTable.Status.Retry', tableModels.Status.Refresh.traceOptions)
        .dispatch(),
  }
  // Pending attributes count as loading: columns are not renderable yet, so the
  // table would otherwise show its empty state until they arrive.

  const isStatusLoadingSg = ngCore.computed(
    (
      isListLoading = isLoadingSg(),
      areAttributesLoading = areAttributesLoadingSg(),
    ) => isListLoading || areAttributesLoading,
  )
  const statusSg = ngCore.computed(
    (
      hasError = hasErrorSg(),
      loading = isStatusLoadingSg(),
    ): tableModels.Status => ({
      ...statusWithoutState,
      error: hasError ? {} : undefined,
      loading,
    }),
  )

  // The top-level object is intentionally recreated when a branch changes, but
  // unchanged branch values retain their references. Store selectors can then
  // stop downstream preparation at the first unaffected branch.
  return ngCore.computed(
    (
      tools = toolsSg(),
      columns = columnsSg(),
      layout = layoutSg(),
      pagination = paginationSg(),
      rows = rowsSg(),
      search = searchSg(),
      selection = selectionSg(),
      status = statusSg(),
    ): tableModels.Config<TTypeParams['entity']> => ({
      columns,
      layout,
      pagination,
      rows,
      search,
      selection,
      status,
      tools,
    }),
  )
}

/**
 * Maps optional collection plugins and configured tools to the toolbar.
 *
 * Clipboard tools depend on current selection and paste availability, while
 * the Excel tool depends on download state. Toolbar and create tools come
 * from the individual view so collections can serve multiple presentations.
 */
function getTools<TTypeParams extends ec.models.TypeParams.Constraint>({
  canPaste,
  clipboardPlugin,
  collection,
  excelPlugin,
  isDownloading,
  selectedIds,
  view,
}: getTools.Params<TTypeParams>): tableModels.Tool.Config {
  const tools: tableModels.Tool[] = []

  // Paste is available only when the optional clipboard plugin reports that a
  // valid clipboard payload can currently be applied.
  if (clipboardPlugin && canPaste) {
    tools.push({
      iconClass: 'atn-icon atn-paste atn-blue',
      id: 'clipboard-paste',
      onClick: event =>
        clipboardPlugin.feature.actions.paste
          .from('DsTable.Toolbar.Paste.Clicked')
          .dispatch({
            dialogConfig: {
              offset: { x: 0, y: 34 },
              originPosition: event
                ? clipboard.models.Coordinates.from(event)
                : { x: 0, y: 0 },
            },
          }),
      title: 'Paste',
    })
  }

  // Copy is useful only when the clipboard plugin exists and there is at least
  // one selected entity for it to serialize.
  if (clipboardPlugin && selectedIds.length > 0) {
    tools.push({
      iconClass: 'atn-icon atn-copy atn-blue',
      id: 'clipboard-copy',
      onClick: () =>
        clipboardPlugin.feature.actions.copySelected
          .from('DsTable.Toolbar.Copy.Clicked')
          .dispatch(),
      title: 'Copy',
    })
  }

  tools.push(...(view?.toolbarTools ?? []))

  // Download remains visible while its plugin exists; `loading` lets the
  // toolbar disable duplicate requests during an active download.
  if (excelPlugin) {
    tools.push({
      iconClass: 'atn-icon atn-download atn-blue',
      id: 'excel-download',
      loading: isDownloading,
      onClick: () =>
        excelPlugin.feature.actions.download
          .from('DsTable.Toolbar.ExcelDownload.Clicked')
          .dispatch(),
      title: 'Download',
    })
  }

  const creation = view?.creation
  // `creation` is narrowed into a local constant so the callback closes over a
  // value known to be defined when the create tool is emitted.
  return {
    create: creation
      ? {
          iconClass: 'atn-icon atn-plus atn-blue',
          id: 'create',
          onClick: () => createEntity(collection, creation),
          title: 'Create',
        }
      : undefined,
    toolbar: tools,
  }
}

namespace getTools {
  /** Inputs required to build one immutable toolbar/create tool snapshot. */
  export interface Params<TTypeParams extends ec.models.TypeParams.Constraint> {
    readonly canPaste: boolean | undefined
    readonly clipboardPlugin: clipboard.Plugin<TTypeParams> | undefined
    readonly collection: ec.Collection<TTypeParams>
    readonly excelPlugin: excel.ec.Plugin<TTypeParams> | undefined
    readonly isDownloading: boolean | undefined
    readonly selectedIds: readonly ec.models.EntityId[]
    readonly view: Config.View<TTypeParams> | undefined
  }
}

/**
 * Starts the configured or default entity-collection creation flow.
 *
 * A function-valued creation option receives the canonical edit dispatcher so
 * callers can provide creation input. A boolean `true` uses the default edit
 * flow without an input payload.
 */
function createEntity<TTypeParams extends ec.models.TypeParams.Constraint>(
  collection: ec.Collection<TTypeParams>,
  creation: Config.Creation<TTypeParams>,
): void {
  const editing = collection.plugins.editingPlugin
  const create = (input: TTypeParams['createEntity']) =>
    editing.feature.actions.edit.from('DsTable.Creation.Started').dispatch({
      edit: { input },
      id: ec.plugins.standard.editing.models.CreationId.generate(),
    })

  if (TypeChecker.isFunction(creation)) {
    creation(create)
    return
  }

  editing.feature.actions.edit.from('DsTable.Creation.Started').dispatch({
    id: ec.plugins.standard.editing.models.CreationId.generate(),
  })
}

/**
 * Supplies the stable tool identity required by the core table.
 *
 * Legacy row icon configs use `class` and an array position; the canonical
 * table requires an `id` and `iconClass`, so the row ID/index pair becomes the
 * stable tool identity.
 */
function getRowTool(
  rowId: tableModels.Row.Id,
  toolIndex: number,
  iconConfig: Config.Row.IconConfig,
): tableModels.Tool {
  return {
    disabled: iconConfig.disabled,
    iconClass: iconConfig.class,
    id: `${rowId}-tool-${toolIndex}`,
    loading: iconConfig.loading,
    onClick: iconConfig.onClick,
    title: iconConfig.title,
  }
}

/**
 * Maps an entity-collection attribute and its configured overrides.
 *
 * Cell extraction remains lazy in `getCell`, because the entity and row index
 * are unavailable while the column snapshot is being created. Sort and
 * visibility callbacks dispatch intent back to the owning collection instead
 * of mutating table-local state.
 */
function getColumn<TTypeParams extends ec.models.TypeParams.Constraint>({
  attribute,
  collection,
  config,
  sort,
}: getColumn.Params<TTypeParams>): tableModels.Column<TTypeParams['entity']> {
  const options = config.columnConfigs[attribute.id]
  const isSortable = options?.isSortable ?? attribute.isSortable ?? false

  return {
    // Attribute-level options override collection defaults, then alignment is
    // inferred from the value type when neither supplies an explicit value.
    align: options?.align ?? getColumnAlignment(attribute),
    description: attribute.description,
    // Cell factories are deferred until a concrete row is being rendered.
    getCell: ({ row, rowIndex }) => {
      const value = attribute.getValueFrom(row)
      const context: Config.Cell.Context<TTypeParams> = {
        attribute,
        entity: row,
        rowIdx: rowIndex,
        value,
      }
      const cell =
        options?.cellConfig?.(context) ?? config.cellConfig?.(context)

      return {
        css: cell?.css,
        onClick: cell?.onClick,
        renderer: cell?.component
          ? {
              component: cell.component,
              inputs: cell.inputs,
              outputs: cell.outputs,
              type: 'component',
            }
          : {
              type: 'value',
              valueInfo: attribute.valueInfo,
            },
        value: ents.core.common.value.Raw.isRaw(value) ? value : undefined,
      }
    },
    id: attribute.id,
    label: attribute.name,
    // Visibility is controlled by the attribute plugin; this callback only
    // dispatches the requested intent.
    onVisibilityChange: visible =>
      collection.plugins.attributePlugin.feature.actions[
        visible ? 'showAttribute' : 'hideAttribute'
      ]
        .from(`DsTable.Column-${attribute.id}.Visibility.Changed`)
        .dispatch({ attributeId: attribute.id }),
    resizable: options?.resizable,
    // Sort direction is a snapshot of the current request. Changes dispatch
    // both sort fields through the collection's applied-params actions.
    sort: isSortable
      ? {
          direction:
            sort?.by === attribute.id
              ? sort.descending
                ? 'descending'
                : 'ascending'
              : undefined,
          onChange: direction => {
            const appliedParams =
              collection.plugins.currentListPlugin.appliedParams
            appliedParams.actions.setSortBy
              .from(`DsTable.Column-${attribute.id}.Sort.Changed`)
              .dispatch(attribute.id)
            appliedParams.actions.setDescending
              .from(`DsTable.Column-${attribute.id}.Sort.Changed`)
              .dispatch(direction === 'descending')
          },
        }
      : undefined,
    visible: !attribute.isHidden,
    width: options?.width,
  }
}

namespace getColumn {
  /** Inputs required to map one collection attribute into a table column. */
  export interface Params<TTypeParams extends ec.models.TypeParams.Constraint> {
    readonly attribute: ec.models.Attribute<TTypeParams>
    readonly collection: ec.Collection<TTypeParams>
    readonly config: Config<TTypeParams>
    readonly sort: ec.models.QueryRequest<TTypeParams>['sort']
  }
}

/**
 * Retains the established numeric and boolean alignment defaults.
 *
 * Explicit column alignment wins in `getColumn`; this helper is only consulted
 * when the collection attribute has no configured override.
 */
function getColumnAlignment<
  TTypeParams extends ec.models.TypeParams.Constraint,
>(attribute: ec.models.Attribute<TTypeParams>): tableModels.Column.Alignment {
  if (
    ents.core.common.value.types.Intrinsic.ForNumber.isForNumber(
      attribute.valueInfo?.type,
    )
  ) {
    return 'right'
  }
  if (
    attribute.valueInfo?.type === ents.core.common.value.types.Intrinsic.Boolean
  ) {
    return 'center'
  }
  return 'left'
}
